import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Shield, CreditCard, Calendar, FileText, Bell, User, MessageSquare, Clock, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MemberDashboard() {
  const { user, updateProfile } = useAuth();
  const [payments, setPayments] = useState([]);
  const [news, setNews] = useState([]);
  const [events, setEvents] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [profile, setProfile] = useState({ name: '', phone: '', address: '' });
  const [supportMsg, setSupportMsg] = useState('');

  useEffect(() => {
    if (!user) return;
    setProfile({ name: user.name || '', phone: user.phone || '', address: user.address || '' });
    Promise.all([
      axios.get(`${API}/membership/payments`, { withCredentials: true }).then(r => setPayments(r.data)).catch(() => {}),
      axios.get(`${API}/news`).then(r => setNews(r.data.slice(0, 5))).catch(() => {}),
      axios.get(`${API}/events`).then(r => setEvents(r.data)).catch(() => {}),
      axios.get(`${API}/announcements`).then(r => setAnnouncements(r.data)).catch(() => {}),
      axios.get(`${API}/documents`, { withCredentials: true }).then(r => setDocuments(r.data)).catch(() => {}),
    ]);
  }, [user]);

  const handleProfileUpdate = async () => {
    try {
      await updateProfile(profile);
      toast.success('Profile updated');
    } catch { toast.error('Update failed'); }
  };

  const handleSupport = async () => {
    if (!supportMsg.trim()) return;
    try {
      await axios.post(`${API}/contact`, { name: user.name, email: user.email, subject: 'Member Support Request', message: supportMsg }, { withCredentials: true });
      toast.success('Support request sent');
      setSupportMsg('');
    } catch { toast.error('Failed to send'); }
  };

  const statusColor = { active: 'bg-forest text-white', pending: 'bg-gold text-navy', expired: 'bg-red-500 text-white' };

  return (
    <div className="pt-16 min-h-screen bg-[#FAFAFA]" data-testid="member-dashboard">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10">
        {/* Welcome Header */}
        <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6" data-testid="dashboard-welcome">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-heading font-bold text-navy">Welcome, {user?.name}</h1>
              <p className="text-sm text-gray-600 font-body">Member since {user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}</p>
            </div>
            <div className="flex items-center gap-3">
              <Badge className={statusColor[user?.membership_status] || statusColor.pending} data-testid="membership-status-badge">
                {(user?.membership_status || 'pending').toUpperCase()}
              </Badge>
              <p className="font-signature text-gold text-xl">Safe Florida</p>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {[
            { label: 'Status', value: (user?.membership_status || 'Pending'), icon: Shield, color: 'text-navy' },
            { label: 'Start Date', value: user?.membership_start ? new Date(user.membership_start).toLocaleDateString() : 'N/A', icon: Calendar, color: 'text-forest' },
            { label: 'Next Billing', value: user?.next_billing ? new Date(user.next_billing).toLocaleDateString() : 'N/A', icon: Clock, color: 'text-gold' },
            { label: 'Payments', value: payments.length, icon: CreditCard, color: 'text-navy' },
          ].map(({ label, value, icon: Icon, color }) => (
            <div key={label} className="bg-white border border-gray-200 rounded-lg p-4">
              <Icon className={`w-5 h-5 ${color} mb-2`} />
              <p className="text-xs text-gray-500 font-body">{label}</p>
              <p className="text-sm font-body font-bold text-navy capitalize">{String(value)}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <Tabs defaultValue="overview" className="space-y-4">
          <TabsList className="bg-white border border-gray-200 rounded-lg p-1" data-testid="dashboard-tabs">
            <TabsTrigger value="overview" className="font-body text-sm" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="payments" className="font-body text-sm" data-testid="tab-payments">Payments</TabsTrigger>
            <TabsTrigger value="documents" className="font-body text-sm" data-testid="tab-documents">Documents</TabsTrigger>
            <TabsTrigger value="profile" className="font-body text-sm" data-testid="tab-profile">Profile</TabsTrigger>
            <TabsTrigger value="support" className="font-body text-sm" data-testid="tab-support">Support</TabsTrigger>
          </TabsList>

          {/* Overview */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Announcements */}
              <div className="bg-white border border-gray-200 rounded-lg p-5">
                <h3 className="font-heading font-bold text-navy text-sm mb-4 flex items-center gap-2"><Bell className="w-4 h-4 text-gold" /> Announcements</h3>
                {announcements.length === 0 ? <p className="text-sm text-gray-500 font-body">No announcements</p> : (
                  <div className="space-y-3">
                    {announcements.slice(0, 3).map(a => (
                      <div key={a.id} className="border-l-2 border-gold pl-3">
                        <p className="text-sm font-body font-medium text-navy">{a.title}</p>
                        <p className="text-xs text-gray-600 font-body">{a.message}</p>
                        <p className="text-xs text-gray-400 font-body mt-1">{new Date(a.created_at).toLocaleDateString()}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* News Feed */}
              <div className="bg-white border border-gray-200 rounded-lg p-5">
                <h3 className="font-heading font-bold text-navy text-sm mb-4 flex items-center gap-2"><FileText className="w-4 h-4 text-forest" /> Latest News</h3>
                {news.length === 0 ? <p className="text-sm text-gray-500 font-body">No news yet</p> : (
                  <div className="space-y-3">
                    {news.map(n => (
                      <div key={n.id} className="border-b border-gray-100 pb-2 last:border-0">
                        <p className="text-sm font-body font-medium text-navy">{n.title}</p>
                        <p className="text-xs text-gray-500 font-body">{new Date(n.created_at).toLocaleDateString()}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Events */}
            <div className="bg-white border border-gray-200 rounded-lg p-5">
              <h3 className="font-heading font-bold text-navy text-sm mb-4 flex items-center gap-2"><Calendar className="w-4 h-4 text-navy" /> Upcoming Events</h3>
              {events.length === 0 ? <p className="text-sm text-gray-500 font-body">No upcoming events</p> : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {events.map(e => (
                    <div key={e.id} className="border border-gray-200 rounded-lg p-4">
                      <p className="text-sm font-body font-bold text-navy">{e.title}</p>
                      <p className="text-xs text-gold font-body mt-1">{e.date}</p>
                      <p className="text-xs text-gray-500 font-body">{e.location}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Payments */}
          <TabsContent value="payments">
            <div className="bg-white border border-gray-200 rounded-lg p-5" data-testid="payments-table">
              <h3 className="font-heading font-bold text-navy text-sm mb-4">Payment History</h3>
              {payments.length === 0 ? <p className="text-sm text-gray-500 font-body">No payments yet</p> : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm font-body">
                    <thead><tr className="border-b border-gray-200">
                      <th className="text-left py-2 text-gray-500 font-medium">Date</th>
                      <th className="text-left py-2 text-gray-500 font-medium">Type</th>
                      <th className="text-left py-2 text-gray-500 font-medium">Amount</th>
                      <th className="text-left py-2 text-gray-500 font-medium">PayPal ID</th>
                    </tr></thead>
                    <tbody>
                      {payments.map(p => (
                        <tr key={p.id} className="border-b border-gray-100">
                          <td className="py-2">{new Date(p.created_at).toLocaleDateString()}</td>
                          <td className="py-2 capitalize">{p.type}</td>
                          <td className="py-2 font-bold text-navy">${p.amount}</td>
                          <td className="py-2 text-gray-500 text-xs">{p.paypal_order_id}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Documents */}
          <TabsContent value="documents">
            <div className="bg-white border border-gray-200 rounded-lg p-5">
              <h3 className="font-heading font-bold text-navy text-sm mb-4">Member Documents</h3>
              {documents.length === 0 ? <p className="text-sm text-gray-500 font-body">No documents available yet. Check back soon!</p> : (
                <div className="space-y-3">
                  {documents.map(d => (
                    <div key={d.id} className="flex items-center justify-between border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-navy" />
                        <div>
                          <p className="text-sm font-body font-medium text-navy">{d.title}</p>
                          <p className="text-xs text-gray-500 font-body">{d.category} | {d.description}</p>
                        </div>
                      </div>
                      {d.url && <a href={d.url} target="_blank" rel="noopener noreferrer" className="text-sm text-gold font-body hover:underline">Download</a>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Profile */}
          <TabsContent value="profile">
            <div className="bg-white border border-gray-200 rounded-lg p-5" data-testid="profile-form">
              <h3 className="font-heading font-bold text-navy text-sm mb-4 flex items-center gap-2"><User className="w-4 h-4" /> Profile Settings</h3>
              <div className="max-w-md space-y-4">
                <div>
                  <Label className="font-body text-sm">Full Name</Label>
                  <Input value={profile.name} onChange={e => setProfile(p => ({ ...p, name: e.target.value }))} className="mt-1 font-body" data-testid="profile-name-input" />
                </div>
                <div>
                  <Label className="font-body text-sm">Email</Label>
                  <Input value={user?.email || ''} disabled className="mt-1 font-body bg-gray-50" />
                </div>
                <div>
                  <Label className="font-body text-sm">Phone</Label>
                  <Input value={profile.phone} onChange={e => setProfile(p => ({ ...p, phone: e.target.value }))} className="mt-1 font-body" data-testid="profile-phone-input" />
                </div>
                <div>
                  <Label className="font-body text-sm">Address</Label>
                  <Input value={profile.address} onChange={e => setProfile(p => ({ ...p, address: e.target.value }))} className="mt-1 font-body" data-testid="profile-address-input" />
                </div>
                <Button onClick={handleProfileUpdate} className="btn-primary-safe font-body" data-testid="profile-save-btn">Save Changes</Button>
              </div>
            </div>
          </TabsContent>

          {/* Support */}
          <TabsContent value="support">
            <div className="bg-white border border-gray-200 rounded-lg p-5">
              <h3 className="font-heading font-bold text-navy text-sm mb-4 flex items-center gap-2"><MessageSquare className="w-4 h-4" /> Contact Support</h3>
              <div className="max-w-md space-y-4">
                <textarea value={supportMsg} onChange={e => setSupportMsg(e.target.value)} rows={5}
                  className="w-full border border-gray-200 rounded-lg p-3 font-body text-sm focus:ring-2 focus:ring-gold/50 focus:border-gold outline-none"
                  placeholder="Describe your question or issue..." data-testid="support-message-input" />
                <Button onClick={handleSupport} className="btn-primary-safe font-body" data-testid="support-send-btn">
                  <Send className="w-4 h-4 mr-2" /> Send Message
                </Button>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
